// Cache management utilities for Pochoclo.tv

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class MemoryCache {
  private cache = new Map<string, CacheItem<any>>();
  private maxSize = 100; // Increased for better cache hit ratio
  private maxMemoryMB = 25; // Increased memory limit for better performance
  private currentMemoryUsage = 0;

  set<T>(key: string, data: T, ttlMs: number = 5 * 60 * 1000): void {
    // Estimate memory usage of the data
    const estimatedSize = this.estimateSize(data);
    
    // Check if adding this item would exceed memory limit
    if (this.currentMemoryUsage + estimatedSize > this.maxMemoryMB * 1024 * 1024) {
      console.warn('🧹 Cache memory limit reached, clearing old items');
      this.clearOldestItems(Math.ceil(this.maxSize * 0.3)); // Clear 30% of items
    }
    
    // Remove oldest items if cache is full
    if (this.cache.size >= this.maxSize) {
      this.clearOldestItems(1);
    }

    const now = Date.now();
    
    // If key already exists, subtract its old size
    const existingItem = this.cache.get(key);
    if (existingItem) {
      this.currentMemoryUsage -= this.estimateSize(existingItem.data);
    }
    
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttlMs,
    });
    
    // Add new size to memory usage
    this.currentMemoryUsage += estimatedSize;
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now > item.expiresAt) {
      this.cache.delete(key);
      this.currentMemoryUsage -= this.estimateSize(item.data);
      return null;
    }

    return item.data;
  }

  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const now = Date.now();
    if (now > item.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    const item = this.cache.get(key);
    if (item) {
      this.currentMemoryUsage -= this.estimateSize(item.data);
    }
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.currentMemoryUsage = 0;
  }

  size(): number {
    return this.cache.size;
  }
  
  getMemoryUsage(): { usedMB: number; maxMB: number; percentage: number } {
    const usedMB = this.currentMemoryUsage / (1024 * 1024);
    return {
      usedMB: Math.round(usedMB * 100) / 100,
      maxMB: this.maxMemoryMB,
      percentage: Math.round((usedMB / this.maxMemoryMB) * 100)
    };
  }
  
  private estimateSize(data: any): number {
    try {
      // Rough estimation of object size in bytes
      const jsonString = JSON.stringify(data);
      return jsonString.length * 2; // UTF-16 uses 2 bytes per character
    } catch {
      return 1024; // Default 1KB if can't stringify
    }
  }
  
  private clearOldestItems(count: number): void {
    const entries = Array.from(this.cache.entries());
    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    for (let i = 0; i < Math.min(count, entries.length); i++) {
      const [key, item] = entries[i];
      this.currentMemoryUsage -= this.estimateSize(item.data);
      this.cache.delete(key);
    }
  }
  
  // Cleanup expired items
  cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiresAt) {
        expiredKeys.push(key);
        this.currentMemoryUsage -= this.estimateSize(item.data);
      }
    }
    
    expiredKeys.forEach(key => this.cache.delete(key));
    
    if (expiredKeys.length > 0) {
      console.log(`🧹 Cleaned up ${expiredKeys.length} expired cache items`);
    }
  }
}

// Global cache instances
export const memoryCache = new MemoryCache();

// Cache keys
export const CACHE_KEYS = {
  CONTENT_LIST: (country: string) => `content_list_${country}`,
  CONTENT_DETAIL: (id: string) => `content_detail_${id}`,
  SEARCH_RESULTS: (query: string, country: string) => `search_${query}_${country}`,
  TMDB_SEARCH: (query: string, country: string) => `tmdb_search_${query}_${country}`,
  STREAMING_PLATFORMS: 'streaming_platforms',
  COUNTRY_AVAILABILITY: (contentId: string, country: string) => `availability_${contentId}_${country}`,
  USER_SEARCH_HISTORY: (country: string) => `user_search_history_${country}`,
} as const;

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  CONTENT_LIST: 15 * 60 * 1000, // 15 minutes (increased for better performance)
  CONTENT_DETAIL: 30 * 60 * 1000, // 30 minutes
  SEARCH_RESULTS: 10 * 60 * 1000, // 10 minutes (increased)
  TMDB_SEARCH: 15 * 60 * 1000, // 15 minutes
  STREAMING_PLATFORMS: 60 * 60 * 1000, // 1 hour
  COUNTRY_AVAILABILITY: 30 * 60 * 1000, // 30 minutes (increased)
  USER_SEARCH_HISTORY: 5 * 60 * 1000, // 5 minutes (shorter since it changes more frequently)
} as const;

// Cache invalidation utilities
export function invalidateContentCache(country?: string): void {
  if (country) {
    // Invalidate cache for specific country
    memoryCache.delete(CACHE_KEYS.CONTENT_LIST(country));
    console.log(`🗑️ Invalidated content cache for country: ${country}`);
  } else {
    // Invalidate all content caches by clearing all keys that match the pattern
    const allKeys = Array.from((memoryCache as any).cache.keys());
    const contentKeys = allKeys.filter(key => key.startsWith('content_list_'));
    contentKeys.forEach(key => memoryCache.delete(key));
    console.log(`🗑️ Invalidated all content caches (${contentKeys.length} entries)`);
  }
}

export function invalidateContentDetailCache(contentId: string): void {
  memoryCache.delete(CACHE_KEYS.CONTENT_DETAIL(contentId));
  console.log(`🗑️ Invalidated content detail cache for: ${contentId}`);
}

export function invalidateUserSearchHistoryCache(country: string): void {
  memoryCache.delete(CACHE_KEYS.USER_SEARCH_HISTORY(country));
  console.log(`🗑️ Invalidated user search history cache for: ${country}`);
}

// Browser storage cache utilities

// Image preloading utilities
export class ImagePreloader {
  private static preloadedImages = new Set<string>();

  static preload(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.preloadedImages.has(src)) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        this.preloadedImages.add(src);
        resolve();
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  static preloadBatch(sources: string[]): Promise<void[]> {
    return Promise.allSettled(sources.map(src => this.preload(src)))
      .then(results => {
        const successful = results.filter(result => result.status === 'fulfilled');
        console.log(`Preloaded ${successful.length}/${sources.length} images`);
        return successful.map(() => undefined);
      });
  }

  static isPreloaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }
}

// Resource hints utilities
export class ResourceHints {
  static preloadResource(href: string, as: string, type?: string): void {
    if (typeof document === 'undefined') return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    
    document.head.appendChild(link);
  }

  static prefetchResource(href: string): void {
    if (typeof document === 'undefined') return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    
    document.head.appendChild(link);
  }

  static preconnect(href: string, crossorigin?: boolean): void {
    if (typeof document === 'undefined') return;

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (crossorigin) link.crossOrigin = 'anonymous';
    
    document.head.appendChild(link);
  }
}

// Initialize resource hints for critical domains
if (typeof document !== 'undefined') {
  // Preconnect to critical domains
  ResourceHints.preconnect('https://image.tmdb.org');
  ResourceHints.preconnect('https://images.unsplash.com');
  
  // Preconnect to Supabase
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl) {
    ResourceHints.preconnect(supabaseUrl);
  }
  
  // Set up periodic cache cleanup
  setInterval(() => {
    memoryCache.cleanup();
  }, 15 * 60 * 1000); // Every 15 minutes (reduced frequency)
  
  // Log cache usage periodically in development
  if (import.meta.env.DEV) {
    setInterval(() => {
      console.log('📊 Cache usage:', memoryCache.getMemoryUsage());
    }, 5 * 60 * 1000); // Every 5 minutes in dev (reduced frequency)
  }
}